import i18n from "i18next"
import { initReactI18next } from "react-i18next"
import LanguageDetector from "i18next-browser-languagedetector"

import pt from "./locales/pt.json"
import ptPT from "./locales/pt-PT.json"
import en from "./locales/en.json"
import es from "./locales/es.json"
import esES from "./locales/es-ES.json"
import nl from "./locales/nl.json"
import fr from "./locales/fr.json"
import de from "./locales/de.json"
import it from "./locales/it.json"

/**
 * Configuração de internacionalização (i18next).
 *
 * Os textos ficam em src/i18n/locales/*.json. O português (Brasil) é o idioma
 * de referência (fallback). Para adicionar um idioma novo, crie um
 * locales/<código>.json e registre-o em `resources` + `supportedLanguages`.
 *
 * O idioma escolhido é detectado/persistido em localStorage (chave i18nextLng).
 */
/**
 * `country` é o código ISO 3166-1 alpha-2 usado pela biblioteca flag-icons
 * (bandeira SVG). `short` é o rótulo curto (código) exibido. A bandeira
 * diferencia as variantes regionais (ex.: PT 🇧🇷 vs PT 🇵🇹, ES 🇦🇷 vs ES 🇪🇸).
 * Para francês/alemão a referência é França/Alemanha (não a Bélgica) e o
 * espanhol latino usa a bandeira da Argentina.
 */
/**
 * `name` é o endônimo — o nome do idioma NO próprio idioma. Num seletor de
 * texto (parceiro, proprietário, usuário) o par short+bandeira não basta: "PT"
 * aparece duas vezes, e "ES" também.
 */
export const supportedLanguages = [
  { code: "pt", short: "PT", country: "br", name: "Português (Brasil)" },
  { code: "pt-PT", short: "PT", country: "pt", name: "Português (Portugal)" },
  { code: "en", short: "EN", country: "gb", name: "English" },
  { code: "es", short: "ES", country: "ar", name: "Español (Latinoamérica)" },
  { code: "es-ES", short: "ES", country: "es", name: "Español (España)" },
  { code: "it", short: "IT", country: "it", name: "Italiano" },
  { code: "nl", short: "NL", country: "be", name: "Nederlands" },
  { code: "fr", short: "FR", country: "fr", name: "Français" },
  { code: "de", short: "DE", country: "de", name: "Deutsch" },
] as const

/**
 * Resolve um locale BCP-47 (ex.: "pt-BR", "en-GB", "es-AR") a partir do código
 * de idioma do i18n, combinando o idioma com o país de referência da marca.
 * Use para formatar datas/números respeitando o idioma ativo (Intl/toLocaleString).
 */
export function resolveDateLocale(lng?: string): string {
  const code = lng ?? i18n.language
  const lang = (code || "pt").split("-")[0]
  // Match exato do código tem prioridade; senão, cai no primeiro do mesmo idioma.
  const match =
    supportedLanguages.find((l) => l.code === code) ??
    supportedLanguages.find((l) => l.code.split("-")[0] === lang)
  const country = (match?.country ?? "br").toUpperCase()
  return `${lang}-${country}`
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      pt: { translation: pt },
      "pt-PT": { translation: ptPT },
      en: { translation: en },
      es: { translation: es },
      "es-ES": { translation: esES },
      it: { translation: it },
      nl: { translation: nl },
      fr: { translation: fr },
      de: { translation: de },
    },
    fallbackLng: "pt",
    supportedLngs: supportedLanguages.map((l) => l.code),
    // pt-BR/pt-* caem para pt; en-US/en-* caem para en, etc.
    nonExplicitSupportedLngs: true,
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
  })

export default i18n
